import { collection, getDocs, doc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { RoleConfig, UserRole, Permission, PermissionValue } from '../types';

// In-memory cache for roles
interface RoleCache {
  roles: RoleConfig[];
  rolesMap: Map<UserRole, RoleConfig>;
  lastFetched: number | null;
}

const roleCache: RoleCache = {
  roles: [],
  rolesMap: new Map(),
  lastFetched: null,
};

// Cache duration: 5 minutes (roles change infrequently)
const CACHE_DURATION = 5 * 60 * 1000;

/** Helper to safely convert any date format to Date object */
function toDate(val: any): Date {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
  if (typeof val === 'number') return new Date(val);
  if (typeof val === 'string') return new Date(val);
  return new Date();
}

/**
 * Check if the cache is still valid
 */
function isCacheValid(): boolean {
  if (!roleCache.lastFetched) return false;
  return false;
  //todo remove false
//   return Date.now() - roleCache.lastFetched < CACHE_DURATION;
}

/**
 * Fetch all roles from Firestore and update cache
 */
export async function fetchRoles(forceRefresh = false): Promise<RoleConfig[]> {
  // Return cached data if valid and not forcing refresh
  if (!forceRefresh && isCacheValid() && roleCache.roles.length > 0) {
    return roleCache.roles;
  }

  try {
    const rolesCollection = collection(db, 'roles');
    const rolesQuery = query(
      rolesCollection,
      where('isActive', '==', true),
      orderBy('displayOrder', 'asc')
    );
    
    const snapshot = await getDocs(rolesQuery);
    
    const roles: RoleConfig[] = [];
    const rolesMap = new Map<UserRole, RoleConfig>();
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const role: RoleConfig = {
        id: doc.id as UserRole,
        name: data.name,
        description: data.description,
        permissions: data.permissions || [],
        label: data.label,
        variant: data.variant,
        displayOrder: data.displayOrder,
        isActive: data.isActive,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
      };
      roles.push(role);
      rolesMap.set(role.id, role);
    });

    // Update cache
    roleCache.roles = roles;
    roleCache.rolesMap = rolesMap;
    roleCache.lastFetched = Date.now();

    return roles;
  } catch (error) {
    console.error('Error fetching roles from Firestore:', error);
    
    // If cache exists, return it even if expired
    if (roleCache.roles.length > 0) {
      console.warn('Returning cached roles due to fetch error');
      return roleCache.roles;
    }
    
    // Fallback to empty array
    return [];
  }
}

/**
 * Get a specific role by ID from cache or Firestore
 */
export async function getRoleById(roleId: UserRole): Promise<RoleConfig | null> {
  // Try to get from cache first
  if (isCacheValid() && roleCache.rolesMap.has(roleId)) {
    return roleCache.rolesMap.get(roleId) || null;
  }

  // Fetch all roles to update cache
  await fetchRoles();
  
  return roleCache.rolesMap.get(roleId) || null;
}

/**
 * Get all active roles (from cache if valid)
 */
export async function getAllRoles(): Promise<RoleConfig[]> {
  return fetchRoles();
}

/**
 * Get roles with specific permission
 */
export async function getRolesWithPermission(permission: PermissionValue): Promise<RoleConfig[]> {
  const roles = await fetchRoles();
  return roles.filter(role => role.permissions.includes(permission));
}

/**
 * Check if a role has a specific permission
 */
export async function roleHasPermission(roleId: UserRole, permission: PermissionValue): Promise<boolean> {
  const role = await getRoleById(roleId);
  return role ? role.permissions.includes(permission) : false;
}

/**
 * Check if any of the user's roles has a specific permission
 */
export async function userHasPermission(userRoles: UserRole[], permission: PermissionValue): Promise<boolean> {
  if (!userRoles || userRoles.length === 0) return false;
  
  const roles = await fetchRoles();
  
  return userRoles.some(userRole => {
    const role = roles.find(r => r.id === userRole);
    return role ? role.permissions.includes(permission) : false;
  });
}

/**
 * Get all permissions for a user based on their roles
 */
export async function getUserPermissions(userRoles: UserRole[]): Promise<PermissionValue[]> {
  if (!userRoles || userRoles.length === 0) return [];
  
  const roles = await fetchRoles();
  const permissionsSet = new Set<PermissionValue>();
  
  userRoles.forEach(userRole => {
    const role = roles.find(r => r.id === userRole);
    if (role) {
      role.permissions.forEach(permission => permissionsSet.add(permission));
    }
  });
  
  return Array.from(permissionsSet);
}

/**
 * Get role configuration for display purposes (label, variant, etc.)
 */
export async function getRoleDisplayConfig(roleId: UserRole): Promise<{ label: string; variant: string } | null> {
  const role = await getRoleById(roleId);
  if (!role) return null;
  
  return {
    label: role.label,
    variant: role.variant,
  };
}

/**
 * Get all roles formatted for dropdown/select components
 */
export async function getRolesForSelect(): Promise<Array<{ value: UserRole; label: string; description: string }>> {
  const roles = await fetchRoles();
  return roles.map(role => ({
    value: role.id,
    label: role.label,
    description: role.description,
  }));
}

/**
 * Clear the role cache (useful for testing or manual refresh)
 */
export function clearRoleCache(): void {
  roleCache.roles = [];
  roleCache.rolesMap.clear();
  roleCache.lastFetched = null;
}

/**
 * Get cache status (useful for debugging)
 */
export function getRoleCacheStatus(): { isCached: boolean; itemCount: number; age: number | null } {
  return {
    isCached: isCacheValid(),
    itemCount: roleCache.roles.length,
    age: roleCache.lastFetched ? Date.now() - roleCache.lastFetched : null,
  };
}
